import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { questions } from "../../data/questions";
import { scrollToTop } from "../../utils/Constants";
import slug from "../../utils/slug";
import { useAuth0 } from "@auth0/auth0-react";
import { API_BASE } from "../../config/apiBase";
import "../../styles/pages/interview/_home-tab.scss";
import TooltipButton from "../../component/template/TooltipButton";
import { readActivity } from "../../utils/activity";
import {
    getInterviewRoleLabel,
    hasInterviewRole,
} from "../../utils/interviewRoles";
import useRecordings from "../../hooks/useRecordings";
import formatDateTime from "../../utils/formatDateTime";

// ─── Tips data ────────────────────────────────────────────────────────────────
const TIPS = [
    "Use STAR: one line each for Situation, Task, Action, Result.",
    "Keep answers clear, concise, and structured.",
    "Use numbers: time saved, revenue, conversion, or customer impact.",
    "Say what you learned and how you'd do it faster next time.",
    "Credit the team, but be clear about your contribution.",
    "Avoid acronyms or explain them briefly.",
    "Ask clarifying questions to narrow scope.",
    "Mirror language from the job description.",
    "Link your answer to the company's goals or product.",
    "Share what you learned and what you'd do differently.",
    "Demonstrate how you give and receive feedback.",
    "Show ownership from problem to measurable outcome.",
    "State assumptions and constraints up front.",
    "Consider edge cases, security, and performance.",
    "Think aloud so interviewers can follow your logic.",
    "If you don't know something, say how you'd find out.",
    "Finish by linking your skills to the role's impact.",
    "Lead with the outcome, then walk backward through the steps.",
    "Frame tradeoffs: speed vs. quality, scope vs. depth.",
    'Replace "we" with "I" when describing your specific work.',
    "Use a simple structure: problem, approach, result.",
    "Highlight constraints (budget, time, tools) to show realism.",
    "Call out what you'd validate next if you had more time.",
    "Use the company's metrics and vocabulary when possible.",
    "Bring one follow-up question to show curiosity.",
    "If stuck, ask to restate the question in your own words.",
    "Close with the impact and who benefited.",
    "Describe how you measured success, not just what you did.",
    "Show initiative: what you started without being asked.",
    "Use one concrete example instead of multiple vague ones.",
];

const formatScore = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    const truncated = Math.trunc(n * 10) / 10;
    return truncated.toFixed(1);
};

// ─── SVG icons ────────────────────────────────────────────────────────────────
const IconRecordings = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M15 10l4.553 -2.276a1 1 0 0 1 1.447 .894v6.764a1 1 0 0 1 -1.447 .894l-4.553 -2.276v-4z" />
        <path d="M3 6m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
    </svg>
);

const IconProgress = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M15.02 19.52c-2.341 .736 -5 .606 -7.32 -.52l-4.7 1l1.3 -3.9c-2.324 -3.437 -1.426 -7.872 2.1 -10.374c3.526 -2.501 8.59 -2.296 11.845 .48c1.649 1.407 2.575 3.253 2.742 5.152" />
        <path d="M19 22v.01" />
        <path d="M19 19a2.003 2.003 0 0 0 .914 -3.782a1.98 1.98 0 0 0 -2.414 .483" />
    </svg>
);

const IconScore = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        strokeWidth="1"
        aria-hidden="true"
    >
        <path
            d="M10 2l2.09 5.26L17 8.27l-3.64 3.55.86 5.18L10 14.25 5.78 17l.86-5.18L3 8.27l4.91-.01L10 2z"
            stroke="currentColor"
            strokeLinejoin="round"
        />
    </svg>
);

const IconTip = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        strokeWidth="1"
        aria-hidden="true"
    >
        <path
            d="M10 2a6 6 0 0 1 3 11.2V15a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-1.8A6 6 0 0 1 10 2z"
            stroke="currentColor"
        />
        <path d="M8 17h4" stroke="currentColor" strokeLinecap="round" />
    </svg>
);

const IconActivity = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        strokeWidth="1"
        aria-hidden="true"
    >
        <path
            d="M1 8h2l2-5 2 10 2-6 1 3h3"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconArrow = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2.5 7h9M8 3.5L11.5 7 8 10.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconNext = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M5 3l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Subcomponents ────────────────────────────────────────────────────────────
function MetricCard({ icon, label, badge, children, className = "" }) {
    return (
        <div className={`metric-card ${className}`}>
            <div className="metric-card__header">
                <span className="metric-card__icon" aria-hidden="true">
                    {icon}
                </span>
                <span className="metric-card__label">{label}</span>
                {badge && <span className="metric-card__badge">{badge}</span>}
            </div>
            <div className="metric-card__body">{children}</div>
        </div>
    );
}

function ProgressRing({ pct, size = 80, stroke = 7 }) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const off = c - (pct / 100) * c;

    return (
        <div className="progress-ring" style={{ width: size, height: size }}>
            <svg width={size} height={size} aria-hidden="true">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={stroke}
                    className="progress-ring__track"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={stroke}
                    strokeDasharray={c}
                    strokeDashoffset={off}
                    strokeLinecap="round"
                    className="progress-ring__fill"
                    style={{
                        transform: "rotate(-90deg)",
                        transformOrigin: "50% 50%",
                    }}
                />
            </svg>
            <span className="progress-ring__label">{pct}%</span>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function HomeTab() {
    const navigate = useNavigate();
    const { user, getAccessTokenSilently } = useAuth0();
    const [profile, setProfile] = useState(null);
    const [token, setToken] = useState(null);
    const [serverAvgScore, setServerAvgScore] = useState(null);
    const isFirstLogin = Number(profile?.login_count) <= 1;

    // Fetch profile / token
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const t = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                if (!cancelled) setToken(t);

                const res = await fetch(
                    `${API_BASE}/profile`,
                    {
                        headers: { Authorization: `Bearer ${t}` },
                    }
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setProfile(data);
            } catch {
                /* noop */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [getAccessTokenSilently]);

    useEffect(() => {
        const handler = (e) => {
            const next = e?.detail;
            if (next && typeof next === "object") {
                setProfile((prev) => ({ ...(prev || {}), ...next }));
            }
        };
        window.addEventListener("mooz:profileUpdated", handler);
        return () => window.removeEventListener("mooz:profileUpdated", handler);
    }, []);

    useEffect(() => {
        if (!token) {
            setServerAvgScore(null);
            return;
        }

        const ac = new AbortController();
        (async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/me/stats`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: ac.signal,
                    }
                );
                if (!res.ok) throw new Error("Stats fetch failed");
                const data = await res.json();
                if (!ac.signal.aborted) {
                    setServerAvgScore(
                        data?.avg_score == null ? null : Number(data.avg_score)
                    );
                }
            } catch {
                if (!ac.signal.aborted) setServerAvgScore(null);
            }
        })();

        return () => ac.abort();
    }, [token]);

    // Local state
    const [recordings, setRecordings] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("recordings") || "[]");
        } catch {
            return [];
        }
    });
    const [answers, setAnswers] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("answers") || "{}");
        } catch {
            return {};
        }
    });
    const [activity, setActivity] = useState(() => readActivity());

    // Sync across tabs
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "recordings") {
                try {
                    setRecordings(JSON.parse(e.newValue || "[]"));
                } catch {}
            }
            if (e.key === "answers") {
                try {
                    setAnswers(JSON.parse(e.newValue || "{}"));
                } catch {}
            }
            if (e.key === "activity") {
                setActivity(readActivity());
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    // Derived: recordings
    const { items: serverRecordings } = useRecordings({ token });
    const recordingsForStats = useMemo(() => {
        if (token && Array.isArray(serverRecordings) && serverRecordings.length)
            return serverRecordings;
        return recordings;
    }, [token, serverRecordings, recordings]);

    const recordedUnique = useMemo(() => {
        const s = new Set();
        recordingsForStats.forEach((rec) => {
            const q =
                rec.question_slug ||
                rec.question ||
                rec.questionTitle ||
                rec.question_title;
            if (q) s.add(slug(q));
        });
        return s.size;
    }, [recordingsForStats]);

    const totalRecordings = recordingsForStats.length;

    const averageScore = useMemo(() => {
        if (token) {
            return serverAvgScore == null ? null : serverAvgScore;
        }

        const scores = recordingsForStats
            .map(
                (rec) =>
                    rec?.feedback_score ??
                    rec?.feedback?.score ??
                    rec?.score ??
                    null
            )
            .map((v) => {
                const n = Number(v);
                return Number.isFinite(n) ? n : null;
            })
            .filter((v) => v !== null);
        if (!scores.length) return null;
        return scores.reduce((a, b) => a + b, 0) / scores.length;
    }, [recordingsForStats, serverAvgScore, token]);

    useEffect(() => {
        setActivity(readActivity());
    }, []);

    // Derived: completion
    const totalQuestions = questions.length;
    const completionPct = totalQuestions
        ? Math.round((recordedUnique / totalQuestions) * 100)
        : 0;

    // Tip of the day
    const [tipIndex, setTipIndex] = useState(0);
    useEffect(() => {
        const todayKey = new Date().toISOString().slice(0, 10);
        const storedDay = localStorage.getItem("home:tipDay");
        const storedIdx = Number(localStorage.getItem("home:tipIndex") || 0);
        let idx;
        if (storedDay === todayKey) {
            idx = storedIdx % TIPS.length;
        } else {
            idx = Math.floor(Date.now() / 86400000) % TIPS.length;
            localStorage.setItem("home:tipDay", todayKey);
            localStorage.setItem("home:tipIndex", String(idx));
        }
        setTipIndex(idx);
    }, []);

    const nextTip = useCallback(() => {
        const n = (tipIndex + 1) % TIPS.length;
        setTipIndex(n);
        localStorage.setItem("home:tipIndex", String(n));
        localStorage.setItem(
            "home:tipDay",
            new Date().toISOString().slice(0, 10)
        );
    }, [tipIndex]);

    // Device test
    const [deviceStatus, setDeviceStatus] = useState("idle");
    const testDevices = useCallback(async () => {
        if (!navigator?.mediaDevices?.getUserMedia) {
            setDeviceStatus("fail");
            return;
        }
        try {
            setDeviceStatus("testing");
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true,
            });
            stream.getTracks().forEach((t) => t.stop());
            setDeviceStatus("ok");
        } catch {
            setDeviceStatus("fail");
        }
    }, []);

    // Display name
    const displayName =
        `${profile?.first_name || ""}`.trim() ||
        (user && (`${user.given_name ?? ""}`.trim() || user.name)) ||
        "Moozer";

    const isProAiPlan =
        String(profile?.plan || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_") === "pro_ai";

    // Recent activity
    const recentActivity = useMemo(() => {
        const bySlug = new Map(
            questions.map((q) => [slug(q.id ?? q.question), q.question])
        );
        const recordingIds = new Set(
            recordingsForStats
                .map((r) => r?.id ?? r?.recording_id)
                .filter(Boolean)
                .map(String)
        );
        return activity
            .filter((item) => {
                if (item.type !== "recording") return true;
                if (!item.recording_id) return true;
                return recordingIds.has(String(item.recording_id));
            })
            .slice(0, 5)
            .map((item) => {
                const questionSlug = item.question_slug;
                const questionTitle = questionSlug
                    ? bySlug.get(questionSlug) ||
                      questionSlug.replace(/-/g, " ")
                    : "General";
                const when = item.ts ? formatDateTime(item.ts) : "";

                if (item.type === "recording")
                    return {
                        id: item.id,
                        type: "recording",
                        title: "Recording saved",
                        meta: questionTitle,
                        when,
                        href:
                            item.recording_id && questionSlug
                                ? `/dashboard/questions/${questionSlug}/recording/${item.recording_id}`
                                : "/dashboard/recordings",
                    };
                if (item.type === "draft")
                    return {
                        id: item.id,
                        type: "draft",
                        title: "Draft updated",
                        meta: questionTitle,
                        when,
                        href: questionSlug
                            ? `/dashboard/questions/${questionSlug}`
                            : "/dashboard/questions",
                    };
                return {
                    id: item.id,
                    type: "other",
                    title: "Activity",
                    meta: questionTitle,
                    when,
                    href: "/dashboard",
                };
            });
    }, [activity, recordingsForStats]);

    const deviceLabel = useMemo(() => {
        if (deviceStatus === "ok")
            return (
                <span className="device-label">
                    Device ready{" "}
                    <span
                        className="device-icon device-icon--pass"
                        aria-hidden="true"
                    />
                </span>
            );
        if (deviceStatus === "fail")
            return (
                <span className="device-label">
                    Check failed{" "}
                    <span
                        className="device-icon device-icon--fail"
                        aria-hidden="true"
                    />
                </span>
            );
        if (deviceStatus === "testing") return "Testing…";
        return "Test camera & mic";
    }, [deviceStatus]);

    return (
        <div className="home-tab">
            {/* ── Header ── */}
            <header className="home-header">
                <div className="home-header__greeting">
                    <p className="home-header__eyebrow">Dashboard</p>
                    <h1 className="home-header__title">
                        {isFirstLogin ? (
                            "Hello Moozer!"
                        ) : (
                            <>
                                Welcome back, <em>{displayName}</em>
                            </>
                        )}
                    </h1>
                    {hasInterviewRole(profile?.role) && (
                        <p className="home-header__sub">
                            Preparing for a{" "}
                            <Link
                                to="/profile"
                                className="home-header__role-link"
                            >
                                {getInterviewRoleLabel(profile.role)}
                            </Link>{" "}
                            role
                        </p>
                    )}
                </div>

                <div
                    className="home-header__actions"
                    role="group"
                    aria-label="Quick actions"
                >
                    <TooltipButton
                        label="Question bank"
                        tooltipTitle="Question bank"
                        tooltipBody="A library of real interview questions to practice."
                        className="action-btn action-btn--primary"
                        onClick={() => {
                            scrollToTop();
                            navigate("questions");
                        }}
                    />
                    <TooltipButton
                        label={deviceLabel}
                        tooltipTitle="Device check"
                        tooltipBody="A quick check to make sure your camera and microphone are ready to go."
                        className={`action-btn action-btn--ghost device-btn device-btn--${deviceStatus}`}
                        onClick={testDevices}
                        disabled={deviceStatus === "testing"}
                        ariaLabel="Test camera and microphone"
                    />
                </div>
            </header>

            {/* ── Metrics grid ── */}
            <section className="metrics-grid" aria-label="Your stats">
                <MetricCard icon={<IconRecordings />} label="Videos recorded">
                    <div className="metric-number">{totalRecordings}</div>
                    <div className="metric-sub">total sessions</div>
                </MetricCard>

                <MetricCard icon={<IconProgress />} label="Questions covered">
                    <ProgressRing pct={completionPct} />
                    {/* <div className="metric-sub">
                        {recordedUnique} of {totalQuestions}
                    </div> */}
                </MetricCard>

                <MetricCard
                    icon={<IconScore />}
                    label="Average score"
                    badge="Pro AI"
                >
                    {averageScore !== null ? (
                        <>
                            <div
                                className={`metric-number${
                                    isProAiPlan ? "" : " metric-number--blur"
                                }`}
                            >
                                {formatScore(isProAiPlan ? averageScore : 0)}
                                <span className="metric-denom">/10</span>
                            </div>
                            <div
                                className={`metric-sub${
                                    isProAiPlan ? "" : " metric-sub--blur"
                                }`}
                            >
                                across all attempts
                            </div>
                        </>
                    ) : (
                        <>
                            <div
                                className={`metric-number metric-number--empty${
                                    isProAiPlan ? "" : " metric-number--blur"
                                }`}
                            >
                                {isProAiPlan ? "—" : "0"}
                            </div>
                            <div
                                className={`metric-sub${
                                    isProAiPlan ? "" : " metric-sub--blur"
                                }`}
                            >
                                no scored recordings yet
                            </div>
                        </>
                    )}
                </MetricCard>

                <MetricCard icon={<IconTip />} label="Tip of the day">
                    <p className="tip-text">{TIPS[tipIndex]}</p>
                    <button
                        className="tip-next"
                        onClick={nextTip}
                        aria-label="Next tip"
                    >
                        Next tip <IconNext />
                    </button>
                </MetricCard>
            </section>

            {/* ── Recent activity ── */}
            <section className="activity-section" aria-label="Recent activity">
                <div className="activity-section__header">
                    <span className="activity-section__title">
                        <IconActivity /> Recent activity
                    </span>
                    <Link
                        to="/dashboard/recordings"
                        className="activity-section__all"
                    >
                        View all <IconArrow />
                    </Link>
                </div>

                {recentActivity.length === 0 ? (
                    <div className="activity-empty">
                        <p>
                            No activity yet. Head to the question bank to get
                            started.
                        </p>
                        <button
                            className="action-btn action-btn--primary"
                            onClick={() => navigate("questions")}
                        >
                            Browse questions <IconArrow />
                        </button>
                    </div>
                ) : (
                    <ul className="activity-list" role="list">
                        {recentActivity.map((item) => (
                            <li key={item.id}>
                                <Link to={item.href} className="activity-item">
                                    <span
                                        className={`activity-item__dot activity-item__dot--${item.type}`}
                                        aria-hidden="true"
                                    />
                                    <span className="activity-item__body">
                                        <span className="activity-item__title">
                                            {item.title}
                                        </span>
                                        <span className="activity-item__meta">
                                            {item.meta}
                                        </span>
                                    </span>
                                    <span className="activity-item__right">
                                        <span className="activity-item__when">
                                            {item.when}
                                        </span>
                                        <span
                                            className="activity-item__cta"
                                            aria-hidden="true"
                                        >
                                            View <IconArrow />
                                        </span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
